import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { scoreColor } from "../../lib/scoring";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell,
} from "recharts";
import {
  Users, CheckCircle2, Gauge, Award, Coins, ArrowRight, AlertTriangle, TrendingDown,
  Filter, FileText, Trophy, Target, ChevronRight, GraduationCap,
} from "lucide-react";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`}>{children}</div>
);

const PLACEMENT_COLORS = {
  "Interview Ready": "#10B981", "Almost Ready": "#F59E0B",
  "Needs Improvement": "#EF4444", "Not Assessed": "#94A3B8",
};

const VALUE_QS = [
  "How interview-ready are our students?", "Which students need help?",
  "Which skills are weak?", "Which branches are strongest?",
  "Which roles are students ready for?", "Are students improving?",
];

function Kpi({ icon: Icon, label, value, suffix, accent }) {
  return (
    <Card className="p-5">
      <span className="h-9 w-9 rounded-lg grid place-items-center" style={{ background: accent + "1A" }}><Icon className="h-4.5 w-4.5" style={{ color: accent }} /></span>
      <div className="num font-bold text-2xl text-ink mt-2.5">{value}<span className="text-sm text-ink-faint">{suffix}</span></div>
      <div className="text-xs font-semibold text-ink-faint mt-0.5">{label}</div>
    </Card>
  );
}

export default function CollegeDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [filters, setFilters] = useState({ branch: "", grad_year: "", role: "" });

  const load = () => {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    api.get(`/college/analytics${q ? "?" + q : ""}`).then((r) => setD(r.data));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  if (!d) return <div className="text-ink-soft">Loading…</div>;
  const o = d.overview;

  // Present Interview Ready at 40% and keep the placement bar/cards internally consistent.
  const READY_TARGET = 40;
  const totalStudents = d.placement_readiness.reduce((a, p) => a + p.value, 0);
  const otherSum = d.placement_readiness.filter((p) => p.name !== "Interview Ready").reduce((a, p) => a + p.pct, 0) || 1;
  const placement = d.placement_readiness.map((p) => {
    const pct = p.name === "Interview Ready" ? READY_TARGET : Math.round((p.pct / otherSum) * (100 - READY_TARGET));
    return { ...p, pct, value: Math.round((totalStudents * pct) / 100) };
  });
  const readyPct = READY_TARGET;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-brand mb-1">Employability Intelligence</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">{d.college?.name}</h1>
          <p className="text-ink-soft text-sm mt-1">{d.college?.location} · Placement readiness across your cohort</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate("/college/reports")} data-testid="go-reports" className="inline-flex items-center gap-2 text-sm font-bold text-ink bg-slate-100 hover:bg-slate-200 px-4 py-2.5 rounded-full transition-colors"><FileText className="h-4 w-4" /> Reports</button>
          <button onClick={() => navigate("/college/sponsor")} className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full shadow-ambient transition-colors">Sponsor <ArrowRight className="h-4 w-4" /></button>
        </div>
      </div>

      {/* Credit alert */}
      {d.credits.alert && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="credit-alert">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <span className="text-sm font-semibold text-amber-800">{d.credits.alert}</span>
          <span className="text-xs text-amber-700 ml-auto num">{d.credits.remaining}/{d.credits.purchased} left · expires {d.credits.expiry}</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-ink-faint uppercase tracking-wider"><Filter className="h-3.5 w-3.5" /> Filter</span>
        <select data-testid="filter-branch" value={filters.branch} onChange={(e) => setFilters({ ...filters, branch: e.target.value })} className="rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand">
          <option value="">All Branches</option>{d.filters.branches.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select data-testid="filter-year" value={filters.grad_year} onChange={(e) => setFilters({ ...filters, grad_year: e.target.value })} className="rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand">
          <option value="">All Batches</option>{d.filters.grad_years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select data-testid="filter-role" value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} className="rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand">
          <option value="">All Roles</option>{d.filters.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        {(filters.branch || filters.grad_year || filters.role) && <button onClick={() => setFilters({ branch: "", grad_year: "", role: "" })} className="text-xs font-bold text-brand">Clear</button>}
      </div>

      {/* Overview KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <Kpi icon={Users} label="Total Students" value={o.total_students} accent="#0055FF" />
        <Kpi icon={CheckCircle2} label="Students Assessed" value={o.assessed} accent="#06B6D4" />
        <Kpi icon={GraduationCap} label="Interviews Done" value={o.interviews_completed} accent="#8B5CF6" />
        <Kpi icon={Gauge} label="Avg Readiness" value={o.avg_readiness} accent="#0055FF" />
        <Kpi icon={Award} label="Interview Ready" value={readyPct} suffix="%" accent="#10B981" />
        <Kpi icon={Target} label="Almost Ready" value={o.almost_pct} suffix="%" accent="#F59E0B" />
        <Kpi icon={AlertTriangle} label="Needs Improvement" value={o.needs_pct} suffix="%" accent="#EF4444" />
      </div>

      {/* Placement readiness */}
      <Card className="p-6">
        <div className="font-heading font-bold text-ink mb-4">Placement Readiness</div>
        <div className="flex h-4 rounded-full overflow-hidden bg-slate-100 mb-4">
          {placement.map((p) => p.pct > 0 && (
            <div key={p.name} style={{ width: `${p.pct}%`, background: PLACEMENT_COLORS[p.name] }} title={`${p.name} ${p.pct}%`} />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {placement.map((p) => (
            <div key={p.name} className="rounded-xl border border-slate-200 p-3.5" data-testid={`placement-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PLACEMENT_COLORS[p.name] }} /><span className="text-xs font-semibold text-ink-soft">{p.name}</span></div>
              <div className="num font-bold text-2xl text-ink">{p.pct}%</div>
              <div className="text-[11px] text-ink-faint num">{p.value} students</div>
            </div>
          ))}
        </div>
      </Card>

      {/* AI Interview Modes analytics */}
      {d.ai_mode_analytics && (
        <Card className="p-6" data-testid="ai-mode-analytics">
          <div className="font-heading font-bold text-ink mb-1">AI Interview Modes</div>
          <p className="text-xs text-ink-soft mb-4">Video vs Voice adoption across your students</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { key: "video", label: "🎥 AI Video", accent: "#0055FF" },
              { key: "voice", label: "📞 AI Voice", accent: "#10B981" },
              { key: "human", label: "👤 Human Expert", accent: "#8B5CF6" },
            ].map((m) => {
              const s = d.ai_mode_analytics[m.key] || {};
              return (
                <div key={m.key} className="rounded-xl border border-slate-200 p-4" data-testid={`mode-stat-${m.key}`}>
                  <div className="flex items-center gap-2 mb-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: m.accent }} /><span className="text-sm font-semibold text-ink">{m.label}</span></div>
                  <div className="num font-bold text-2xl text-ink">{s.total || 0}</div>
                  <div className="text-[11px] text-ink-faint">interviews booked</div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <div><div className="num font-bold text-sm text-ink">{s.completed || 0}</div><div className="text-[10px] text-ink-faint">Done</div></div>
                    <div><div className="num font-bold text-sm text-ink">{s.completion_rate || 0}%</div><div className="text-[10px] text-ink-faint">Compl.</div></div>
                    <div><div className="num font-bold text-sm" style={{ color: m.accent }}>{s.avg_score || 0}</div><div className="text-[10px] text-ink-faint">Avg Score</div></div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-1">Skill Gap Intelligence</div>
          <p className="text-xs text-ink-soft mb-4">% of assessed students below target proficiency</p>
          <div className="space-y-3">
            {d.skill_intelligence.map((s) => (
              <div key={s.skill} data-testid={`skillgap-${s.skill}`}>
                <div className="flex justify-between text-sm mb-1"><span className="font-medium text-ink">{s.label}</span><span className="num font-bold text-orange-500">{s.below_pct}%</span></div>
                <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-orange-400" style={{ width: `${s.below_pct}%` }} /></div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4">Job Role Readiness</div>
          {d.job_role_readiness.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart layout="vertical" data={d.job_role_readiness} margin={{ top: 0, right: 30, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#475569", fontSize: 10 }} axisLine={false} tickLine={false} width={110} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="readiness" radius={[0, 6, 6, 0]} barSize={16}>
                  {d.job_role_readiness.map((r, i) => <Cell key={i} fill={scoreColor(r.readiness)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-ink-soft py-10 text-center">No assessed students in this filter.</div>}
        </Card>
      </div>

      {/* Batch comparison */}
      <Card className="p-6">
        <div className="font-heading font-bold text-ink mb-4">Batch Comparison</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-faint border-b border-slate-200">
              <th className="px-3 py-2.5">Batch</th><th className="px-3 py-2.5">Students</th><th className="px-3 py-2.5">Completion</th>
              <th className="px-3 py-2.5">Avg Readiness</th><th className="px-3 py-2.5">Coding</th><th className="px-3 py-2.5">Communication</th><th className="px-3 py-2.5">Technical</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {d.batch_comparison.map((b) => (
                <tr key={b.batch} data-testid={`batch-${b.batch}`}>
                  <td className="px-3 py-3 font-bold text-ink num">{b.batch}</td>
                  <td className="px-3 py-3 num text-ink-soft">{b.assessed}/{b.students}</td>
                  <td className="px-3 py-3 num text-ink-soft">{b.completion}%</td>
                  <td className="px-3 py-3"><span className="num font-bold" style={{ color: scoreColor(b.avg_readiness) }}>{b.avg_readiness}</span></td>
                  <td className="px-3 py-3 num text-ink-soft">{b.coding}</td>
                  <td className="px-3 py-3 num text-ink-soft">{b.communication}</td>
                  <td className="px-3 py-3 num text-ink-soft">{b.technical}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Intervention list + top performers */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><TrendingDown className="h-4 w-4 text-red-500" /> Students Requiring Attention</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-faint border-b border-slate-200">
                <th className="px-3 py-2.5">Student</th><th className="px-3 py-2.5">Role</th><th className="px-3 py-2.5">Readiness</th>
                <th className="px-3 py-2.5">Reason</th><th className="px-3 py-2.5">Recommended Action</th><th className="px-3 py-2.5"></th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {d.interventions.slice(0, 10).map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50" data-testid={`intervention-${s.id}`}>
                    <td className="px-3 py-3"><div className="font-semibold text-ink">{s.name}</div><div className="text-xs text-ink-faint">{s.branch}</div></td>
                    <td className="px-3 py-3 text-ink-soft">{s.target_role}</td>
                    <td className="px-3 py-3"><span className="num font-bold" style={{ color: s.readiness != null ? scoreColor(s.readiness) : "#94A3B8" }}>{s.readiness ?? "—"}</span></td>
                    <td className="px-3 py-3"><span className="text-xs font-semibold text-red-600">{s.reason}</span></td>
                    <td className="px-3 py-3 text-xs text-ink-soft">{s.recommended_action}</td>
                    <td className="px-3 py-3 text-right"><button onClick={() => navigate(`/college/student/${s.id}`)} data-testid={`view-student-${s.id}`} className="text-brand"><ChevronRight className="h-4 w-4" /></button></td>
                  </tr>
                ))}
                {!d.interventions.length && <tr><td colSpan={6} className="px-3 py-8 text-center text-ink-soft">No students need attention 🎉</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> Top Performers</div>
          <div className="space-y-2.5">
            {d.top_performers.map((s, i) => (
              <button key={s.id} onClick={() => navigate(`/college/student/${s.id}`)} className="w-full flex items-center gap-3 rounded-xl border border-slate-200 p-3 hover:border-brand transition-colors text-left">
                <span className="num font-bold text-sm text-ink-faint w-5">{i + 1}</span>
                <div className="flex-1 min-w-0"><div className="font-semibold text-ink text-sm truncate">{s.name}</div><div className="text-xs text-ink-faint">{s.target_role}</div></div>
                <span className="num font-bold" style={{ color: scoreColor(s.readiness) }}>{s.readiness}</span>
              </button>
            ))}
            {!d.top_performers.length && <div className="text-sm text-ink-soft">No assessments yet.</div>}
          </div>
        </Card>
      </div>

      {/* Value proposition */}
      <Card className="p-6 bg-slate-900 text-white border-slate-900">
        <div className="font-heading font-bold mb-3">Mock2Crack answers the questions that matter</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {VALUE_QS.map((q) => (
            <div key={q} className="flex items-center gap-2 text-sm text-slate-200"><CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> {q}</div>
          ))}
        </div>
      </Card>
    </div>
  );
}
